/**
 * Retry utilities for the MoEngage event queue.
 *
 * Strategy: truncated binary exponential backoff with random jitter.
 *
 *   delay(n) = min(BASE × 2^(n-1), MAX_DELAY) + rand(0, JITTER_MAX)
 *
 * This spreads retries over time, avoids a thundering herd when many events
 * fail simultaneously, and ensures we never wait longer than MAX_DELAY.
 */

import {
	MAX_RETRY_ATTEMPTS,
	RETRY_BASE_DELAY_MS,
	RETRY_JITTER_MAX_MS,
	RETRY_MAX_DELAY_MS,
	QUEUE_STATUS,
} from "./constants";

/**
 * Calculate how long to wait before the next retry attempt.
 *
 * @param {number} attempt - 1-based attempt index (1 = first retry).
 * @returns {number} Delay in milliseconds.
 */
export function calculateRetryDelay(attempt) {
	const exponential = RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1);
	const capped = Math.min(exponential, RETRY_MAX_DELAY_MS);
	const jitter = Math.random() * RETRY_JITTER_MAX_MS;
	return Math.round(capped + jitter);
}

/**
 * Whether an event is eligible for another retry attempt.
 *
 * @param {Object} event - Queued event object.
 * @returns {boolean}
 */
export function shouldRetry(event) {
	return (event.retryCount ?? 0) < MAX_RETRY_ATTEMPTS;
}

/**
 * Return a new event object with the retry counter incremented and
 * status reset to PENDING so the next flush will pick it up.
 *
 * @param {Object} event - Queued event object.
 * @returns {Object} Updated event (immutable — original is not mutated).
 */
export function incrementRetry(event) {
	return {
		...event,
		retryCount: (event.retryCount ?? 0) + 1,
		lastRetryAt: new Date().toISOString(),
		// Back to PENDING so the periodic flush interval re-attempts it.
		status: QUEUE_STATUS.PENDING,
	};
}

/**
 * Return a new event object marked as permanently failed.
 * Events in this state will be removed from the queue without retrying.
 *
 * @param {Object} event - Queued event object.
 * @param {string} [reason] - Optional human-readable reason for the failure.
 * @returns {Object} Updated event.
 */
export function markAsFailed(event, reason = "max_retries_exceeded") {
	return {
		...event,
		status: QUEUE_STATUS.FAILED,
		failureReason: reason,
		failedAt: new Date().toISOString(),
	};
}
